import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  ElementRef,
  HostListener,
  inject,
  input,
  output,
  signal,
} from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';
import { catchError, debounceTime, Observable, of, Subject, switchMap } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

export interface ComboOption {
  id: number | string;
  name: string;
  hint?: string;
  icon?: string;
}

let nextId = 0;

/**
 * Searchable choice of the widget editor: a fixed list filtered while typing (options), or a server search
 * (search). Keyboard: arrows, Enter, Escape; a clear button when not required.
 */
@Component({
  selector: 'iu-widget-editor-combo',
  standalone: true,
  imports: [TranslateModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <div class="wec" [class.wec--open]="open()" [class.wec--invalid]="!!error()" [class.wec--disabled]="disabled()" (focusout)="onFocusOut($event)">
      <label class="wec-label" [attr.for]="inputId">
        {{ label() }}
        @if (required()) {
          <span class="wec-required" aria-hidden="true">*</span>
        }
      </label>
      <div class="wec-control">
        <input
          [id]="inputId"
          class="p-inputtext wec-input"
          type="text"
          role="combobox"
          aria-autocomplete="list"
          autocomplete="off"
          [attr.aria-expanded]="open()"
          [attr.aria-controls]="listId"
          [attr.aria-required]="required()"
          [attr.aria-invalid]="!!error()"
          [attr.aria-describedby]="error() ? inputId + '-error' : hint() ? inputId + '-hint' : null"
          [attr.aria-activedescendant]="open() && active() >= 0 ? listId + '-' + active() : null"
          [attr.data-field]="field()"
          [disabled]="disabled()"
          [placeholder]="placeholder()"
          [value]="text()"
          (focus)="openList()"
          (click)="openList()"
          (input)="onInput($any($event.target).value)"
          (keydown)="onKey($event)" />
        @if (value() && !required() && !disabled()) {
          <button type="button" class="wec-clear" tabindex="-1" [attr.aria-label]="'widgetEditor.clear' | translate" (click)="pick(null)">
            <i class="fa-solid fa-xmark" aria-hidden="true"></i>
          </button>
        }
        <i class="fa-solid fa-chevron-down wec-caret" aria-hidden="true"></i>
      </div>
      @if (open()) {
        <ul class="wec-list" role="listbox" [id]="listId">
          @if (loading()) {
            <li class="wec-hint" role="presentation"><i class="fa-solid fa-circle-notch fa-spin" aria-hidden="true"></i></li>
          } @else if (shown().length === 0) {
            <li class="wec-hint" role="presentation">{{ 'widgetEditor.noMatch' | translate }}</li>
          }
          @for (o of shown(); track o.id; let i = $index) {
            <li
              role="option"
              class="wec-option"
              [id]="listId + '-' + i"
              [class.wec-option--active]="i === active()"
              [class.wec-option--selected]="o.id === value()?.id"
              [attr.aria-selected]="o.id === value()?.id"
              (mousedown)="$event.preventDefault()"
              (click)="pick(o)">
              @if (o.icon) {
                <i [class]="o.icon" aria-hidden="true"></i>
              }
              <span class="wec-option-name">{{ o.name }}</span>
              @if (o.hint && o.hint !== o.name) {
                <small>{{ o.hint }}</small>
              }
            </li>
          }
        </ul>
      }
      @if (error()) {
        <small class="wec-error" [id]="inputId + '-error'">{{ error()! | translate }}</small>
      } @else if (hint()) {
        <small class="wec-help" [id]="inputId + '-hint'">{{ hint() }}</small>
      }
    </div>
  `,
  styleUrl: './widget-editor-combo.component.scss',
})
export class WidgetEditorComboComponent {
  readonly label = input('');
  readonly placeholder = input('');
  readonly hint = input('');
  readonly required = input(false);
  readonly disabled = input(false);
  /** i18n key of the problem, shown under the field */
  readonly error = input<string | null | undefined>(null);
  /** data-field of the input, used to focus the first field to fix */
  readonly field = input('');
  readonly value = input<ComboOption | null>(null);
  /** fixed list, filtered while typing */
  readonly options = input<ComboOption[] | null>(null);
  /** server search (q: what is typed) when there is no fixed list */
  readonly search = input<((q: string) => Observable<ComboOption[]>) | null>(null);
  readonly valueChange = output<ComboOption | null>();

  readonly #host = inject<ElementRef<HTMLElement>>(ElementRef);
  readonly inputId = `wec-${++nextId}`;
  readonly listId = `${this.inputId}-list`;
  readonly open = signal(false);
  readonly loading = signal(false);
  readonly active = signal(-1);
  readonly #typed = signal<string | null>(null);
  readonly #found = signal<ComboOption[]>([]);
  readonly #query$ = new Subject<string>();

  /** options on screen: the fixed list filtered by what is typed, or the server answer */
  readonly shown = computed(() => {
    const fixed = this.options();
    if (!fixed) return this.#found();
    const q = fold(this.#typed() ?? '');
    return q ? fixed.filter((o) => fold(o.name).includes(q) || fold(o.hint ?? '').includes(q)) : fixed;
  });

  constructor() {
    this.#query$
      .pipe(
        debounceTime(200),
        switchMap((q) => {
          const fn = this.search();
          if (!fn) return of([] as ComboOption[]);
          this.loading.set(true);
          return fn(q).pipe(catchError(() => of([] as ComboOption[])));
        }),
        takeUntilDestroyed(inject(DestroyRef))
      )
      .subscribe((found) => {
        this.loading.set(false);
        this.#found.set(found);
        this.active.set(found.length ? 0 : -1);
      });
  }

  text(): string {
    return this.#typed() ?? this.value()?.name ?? '';
  }

  openList(): void {
    if (this.disabled() || this.open()) return;
    this.open.set(true);
    if (!this.options()) this.#query$.next('');
    const selected = this.shown().findIndex((o) => o.id === this.value()?.id);
    this.active.set(selected >= 0 ? selected : this.shown().length ? 0 : -1);
  }

  onInput(text: string): void {
    this.#typed.set(text);
    this.open.set(true);
    if (!this.options()) this.#query$.next(text.trim());
    else this.active.set(this.shown().length ? 0 : -1);
  }

  onKey(event: KeyboardEvent): void {
    const n = this.shown().length;
    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        if (!this.open()) this.openList();
        else if (n) this.active.set((this.active() + 1) % n);
        break;
      case 'ArrowUp':
        event.preventDefault();
        if (n) this.active.set((this.active() - 1 + n) % n);
        break;
      case 'Enter': {
        const o = this.shown()[this.active()];
        if (this.open() && o) {
          event.preventDefault();
          this.pick(o);
        }
        break;
      }
      case 'Escape':
        if (this.open()) {
          event.stopPropagation();
          this.close();
        }
        break;
      case 'Tab':
        this.close();
        break;
    }
  }

  pick(option: ComboOption | null): void {
    this.valueChange.emit(option);
    this.close();
  }

  close(): void {
    this.open.set(false);
    this.#typed.set(null);
  }

  onFocusOut(event: FocusEvent): void {
    const next = event.relatedTarget as Node | null;
    if (this.open() && (!next || !this.#host.nativeElement.contains(next))) this.close();
  }

  @HostListener('document:mousedown', ['$event'])
  onOutside(event: MouseEvent): void {
    if (this.open() && !this.#host.nativeElement.contains(event.target as Node)) this.close();
  }
}

/** lower case, no accents */
function fold(value: string): string {
  return value.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();
}
